import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface ArcaneBlitzStats {
  id: string;
  user_id: string;
  total_wins: number;
  total_losses: number;
  total_superpowers_used: number;
  highest_mana_in_game: number;
  tutorial_completed: boolean;
  tutorial_completed_at: string | null;
  last_daily_challenge_date: string | null;
  daily_challenge_streak: number;
  created_at: string;
  updated_at: string;
}

export interface ArcaneLeaderboardEntry {
  user_id: string;
  username: string;
  display_name?: string;
  avatar_config?: any;
  total_wins: number;
  total_superpowers_used: number;
  highest_mana_in_game: number;
  isFake?: boolean;
}

export function useArcaneBlitzStats() {
  const { user } = useAuth();
  const [stats, setStats] = useState<ArcaneBlitzStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState<ArcaneLeaderboardEntry[]>([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('arcane_blitz_stats')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('[ArcaneBlitzStats] fetch error:', error);
        setLoading(false);
        return;
      }

      if (!data) {
        // Initialize row
        const { data: inserted } = await supabase
          .from('arcane_blitz_stats')
          .insert({ user_id: user.id })
          .select()
          .single();
        setStats(inserted);
      } else {
        setStats(data);
      }
    } catch (err) {
      console.error('[ArcaneBlitzStats] error:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const recordGameResult = useCallback(async (won: boolean, superpowersUsed: number, peakMana: number) => {
    if (!user) return;
    try {
      const { data: current } = await supabase
        .from('arcane_blitz_stats')
        .select('total_wins,total_losses,total_superpowers_used,highest_mana_in_game')
        .eq('user_id', user.id)
        .maybeSingle();

      const base = current || { total_wins: 0, total_losses: 0, total_superpowers_used: 0, highest_mana_in_game: 0 };

      await supabase
        .from('arcane_blitz_stats')
        .upsert({
          user_id: user.id,
          total_wins: base.total_wins + (won ? 1 : 0),
          total_losses: base.total_losses + (won ? 0 : 1),
          total_superpowers_used: base.total_superpowers_used + superpowersUsed,
          highest_mana_in_game: Math.max(base.highest_mana_in_game, peakMana),
        }, { onConflict: 'user_id' });

      fetchStats();
    } catch (err) {
      console.error('[ArcaneBlitzStats] recordGameResult error:', err);
    }
  }, [user, fetchStats]);

  const markTutorialComplete = useCallback(async () => {
    if (!user) return;
    try {
      await supabase
        .from('arcane_blitz_stats')
        .upsert({
          user_id: user.id,
          tutorial_completed: true,
          tutorial_completed_at: new Date().toISOString(),
        }, { onConflict: 'user_id' });
      setStats(prev => prev ? { ...prev, tutorial_completed: true } : prev);
    } catch (err) {
      console.error('[ArcaneBlitzStats] markTutorialComplete error:', err);
    }
  }, [user]);

  const claimDailyChallenge = useCallback(async () => {
    if (!user) return;
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data: current } = await supabase
        .from('arcane_blitz_stats')
        .select('daily_challenge_streak,last_daily_challenge_date')
        .eq('user_id', user.id)
        .maybeSingle();

      const base = current || { daily_challenge_streak: 0, last_daily_challenge_date: null };
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      const newStreak = base.last_daily_challenge_date === yesterdayStr
        ? base.daily_challenge_streak + 1
        : 1;

      await supabase
        .from('arcane_blitz_stats')
        .upsert({
          user_id: user.id,
          last_daily_challenge_date: today,
          daily_challenge_streak: newStreak,
        }, { onConflict: 'user_id' });

      fetchStats();
    } catch (err) {
      console.error('[ArcaneBlitzStats] claimDailyChallenge error:', err);
    }
  }, [user, fetchStats]);

  const fetchLeaderboard = useCallback(async () => {
    setLeaderboardLoading(true);
    try {
      const { data, error } = await supabase
        .from('arcane_blitz_stats')
        .select('user_id, total_wins, total_superpowers_used, highest_mana_in_game')
        .order('total_wins', { ascending: false })
        .limit(50);

      if (error) throw error;

      if (!data || data.length === 0) {
        setLeaderboard([]);
        return;
      }

      // Fetch profiles
      const userIds = data.map(d => d.user_id);
      const { data: profiles } = await supabase
        .from('user_profiles_public')
        .select('id, username, display_name, avatar_config')
        .in('id', userIds);

      const profileMap = new Map((profiles || []).map(p => [p.id, p]));

      const entries: ArcaneLeaderboardEntry[] = data.map(d => {
        const profile = profileMap.get(d.user_id);
        return {
          user_id: d.user_id,
          username: profile?.username || 'Player',
          display_name: profile?.display_name,
          avatar_config: profile?.avatar_config,
          total_wins: d.total_wins,
          total_superpowers_used: d.total_superpowers_used,
          highest_mana_in_game: d.highest_mana_in_game,
          isFake: false,
        };
      });

      setLeaderboard(entries);
    } catch (err) {
      console.error('[ArcaneBlitzStats] fetchLeaderboard error:', err);
    } finally {
      setLeaderboardLoading(false);
    }
  }, []);

  return {
    stats,
    loading,
    leaderboard,
    leaderboardLoading,
    fetchStats,
    fetchLeaderboard,
    recordGameResult,
    markTutorialComplete,
    claimDailyChallenge,
  };
}
